import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NONE_ENABLED, capabilitiesApi, type TransitCapabilities } from '@/lib/capabilities-api';
import { capabilityKeys } from '@/lib/query-keys';

interface CapabilitiesState {
  /** Live product lines. All-false until the server confirms (fail-closed). */
  capabilities: TransitCapabilities;
  /** True once the capabilities query has settled (success or error). */
  ready: boolean;
}

const CapabilitiesContext = createContext<CapabilitiesState>({
  capabilities: NONE_ENABLED,
  ready: false,
});

/**
 * Fetches the live-product-line status once for the authed shell. Fail-closed:
 * defaults to NONE_ENABLED (all off) and only reveals a line the server confirms.
 * `ready` lets consumers show a loader while the first fetch is in flight rather
 * than flashing an empty state.
 */
export function CapabilitiesProvider({ children }: { children: ReactNode }) {
  const { data, isPending } = useQuery<TransitCapabilities>({
    queryKey: capabilityKeys.all(),
    queryFn: capabilitiesApi.get,
    staleTime: 5 * 60_000,
  });
  return (
    <CapabilitiesContext.Provider value={{ capabilities: data ?? NONE_ENABLED, ready: !isPending }}>
      {children}
    </CapabilitiesContext.Provider>
  );
}

/** The live ONDC transit product lines + whether the fetch has settled. */
export function useCapabilities(): CapabilitiesState {
  return useContext(CapabilitiesContext);
}
